import {
  Card,
  Checkbox,
  Col,
  Row,
  Tabs,
  Tag,
  Typography,
  Space,
  Badge,
} from "antd";
import Flex from "components/shared-components/Flex";
import React from "react";

import { motion } from "framer-motion";

const { Text } = Typography;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
      duration: 0.4,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 30,
      duration: 0.3,
    },
  },
  hover: {
    scale: 1.015,
    y: -1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 30,
      duration: 0.2,
    },
  },
};

const checkboxVariants = {
  checked: {
    scale: [1, 1.15, 1],
    transition: {
      duration: 0.25,
      ease: "easeOut",
    },
  },
  unchecked: {
    scale: 1,
    transition: {
      duration: 0.15,
      ease: "easeInOut",
    },
  },
};

const CommionPermissonsTab = ({
  UserPermission,
  handlePermissionChange,
  existPermission,
  selectAll,
  permissionGroups,
  groupedPermissions,
  getGroupStats,
  handleGroupSelect
}) => {
  

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Tabs
        type="card"
        size="large"
        animated={{ inkBar: true, tabPane: true }}
        items={[
          // Generate tab items for each permission group
          ...Object.entries(permissionGroups)
            .map(([groupKey, groupConfig]) => {
              const groupPermissions = groupedPermissions[groupKey] || [];
              if (groupPermissions.length === 0) return null;

              const stats = getGroupStats(groupKey);
              const isAllSelected = stats.selected === stats.total && stats.total > 0;
              const isPartiallySelected = stats.selected > 0 && stats.selected < stats.total;

              return {
                key: groupKey,
                label: (
                  <motion.div
                    variants={itemVariants}
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <span style={{ fontSize: "16px" }}>{groupConfig.icon}</span>
                    <Text strong>{groupConfig.name}</Text>
                    <Badge 
                      count={stats.selected} 
                      showZero
                      color={groupConfig.color}
                      style={{ backgroundColor: groupConfig.color }}
                    />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      /{stats.total}
                    </Text>
                  </motion.div>
                ),
                children: (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    style={{ padding: "16px 0" }}
                  >
                    {/* Group Header with Select All */}
                    <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                      <Flex alignItems="center" justifyContent="space-between">
                        <Space>
                          <span style={{ fontSize: "18px" }}>{groupConfig.icon}</span>
                          <Text strong style={{ fontSize: "16px" }}>{groupConfig.name}</Text>
                          <Tag color={groupConfig.color}>
                            {stats.selected}/{stats.total}
                          </Tag>
                        </Space>
                        {UserPermission?.includes("Assign User Permission") && (
                          <motion.div
                            variants={checkboxVariants}
                            animate={isAllSelected ? "checked" : "unchecked"}
                          >
                            <Checkbox
                              checked={isAllSelected}
                              indeterminate={isPartiallySelected}
                              onChange={(e) => {
                                handleGroupSelect(groupKey, e.target.checked);
                              }}
                            >
                              Select All
                            </Checkbox>
                          </motion.div>
                        )}
                      </Flex>
                      <Text type="secondary" style={{ fontSize: "13px", marginTop: "4px", display: "block" }}>
                        {groupConfig.description}
                      </Text>
                    </div>

                    {/* Permissions Grid */}
                    <Row gutter={[16, 16]}>
                      {groupPermissions.map((item, index) => (
                        <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item?.id || index}>
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                          >
                            <Card
                              size="small"
                              hoverable
                              style={{
                                borderColor: (existPermission?.includes(item?.id) || selectAll) 
                                  ? groupConfig.color === 'default' ? '#1677ff' : undefined
                                  : undefined,
                                borderWidth: (existPermission?.includes(item?.id) || selectAll) ? '2px' : '1px'
                              }}
                            >
                              <Flex alignItems="center" justifyContent="space-between">
                                <div style={{ flex: 1 }}>
                                  <Text
                                    strong={existPermission?.includes(item?.id) || selectAll}
                                    style={{ fontSize: "13px" }}
                                  >
                                    {item.name}
                                  </Text>
                                </div>
                                {UserPermission?.includes("Assign User Permission") && (
                                  <motion.div
                                    variants={checkboxVariants}
                                    animate={
                                      existPermission?.includes(item?.id) || selectAll
                                        ? "checked"
                                        : "unchecked"
                                    }
                                  >
                                    <Checkbox
                                      onChange={(e) => handlePermissionChange(e)}
                                      value={item?.id}
                                      checked={existPermission?.includes(item?.id) || selectAll}
                                      id={`checkbox-${groupKey}-${index}`}
                                    />
                                  </motion.div>
                                )}
                              </Flex>
                            </Card>
                          </motion.div>
                        </Col>
                      ))}
                    </Row>
                  </motion.div>
                ),
              };
            })
            .filter(Boolean), // Remove null items

          // Add misc tab if there are ungrouped permissions
          ...(groupedPermissions.misc && groupedPermissions.misc.length > 0
            ? [
                {
                  key: "misc",
                  label: (
                    <motion.div
                      variants={itemVariants}
                      className="d-flex align-items-center"
                      style={{ gap: "8px" }}
                    >
                      <Text strong>Others</Text>
                      <Badge 
                        count={getGroupStats("misc").selected} 
                        showZero
                        color="default"
                      />
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        /{getGroupStats("misc").total}
                      </Text>
                    </motion.div>
                  ),
                  children: (
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      style={{ padding: "16px 0" }}
                    >
                      {/* Misc Group Header */}
                      <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Space>
                            <Text strong style={{ fontSize: "16px" }}>Other Permissions</Text>
                            <Tag color="default">
                              {getGroupStats("misc").selected}/{getGroupStats("misc").total}
                            </Tag>
                          </Space>
                          {UserPermission?.includes("Assign User Permission") && (
                            <motion.div
                              variants={checkboxVariants}
                              animate={
                                getGroupStats("misc").selected === getGroupStats("misc").total &&
                                getGroupStats("misc").total > 0
                                  ? "checked"
                                  : "unchecked"
                              }
                            >
                              <Checkbox
                                checked={
                                  getGroupStats("misc").selected === getGroupStats("misc").total &&
                                  getGroupStats("misc").total > 0
                                }
                                indeterminate={
                                  getGroupStats("misc").selected > 0 &&
                                  getGroupStats("misc").selected < getGroupStats("misc").total
                                }
                                onChange={(e) => {
                                  handleGroupSelect("misc", e.target.checked);
                                }}
                              >
                                Select All
                              </Checkbox>
                            </motion.div>
                          )}
                        </Flex>
                        <Text type="secondary" style={{ fontSize: "13px", marginTop: "4px", display: "block" }}>
                          Permissions that don't fit into specific categories
                        </Text>
                      </div>

                      {/* Misc Permissions Grid */}
                      <Row gutter={[16, 16]}>
                        {groupedPermissions.misc.map((item, index) => (
                          <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item?.id || index}>
                            <motion.div
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                            >
                              <Card
                                size="small"
                                hoverable
                                style={{
                                  borderColor: (existPermission?.includes(item?.id) || selectAll) 
                                    ? '#1677ff' : undefined,
                                  borderWidth: (existPermission?.includes(item?.id) || selectAll) ? '2px' : '1px'
                                }}
                              >
                                <Flex alignItems="center" justifyContent="space-between">
                                  <div style={{ flex: 1 }}>
                                    <Text
                                      strong={existPermission?.includes(item?.id) || selectAll}
                                      style={{ fontSize: "13px" }}
                                    >
                                      {item.name}
                                    </Text>
                                  </div>
                                  {UserPermission?.includes("Assign User Permission") && (
                                    <motion.div
                                      variants={checkboxVariants}
                                      animate={
                                        existPermission?.includes(item?.id) || selectAll
                                          ? "checked"
                                          : "unchecked"
                                      }
                                    >
                                      <Checkbox
                                        onChange={(e) => handlePermissionChange(e)}
                                        value={item?.id}
                                        checked={existPermission?.includes(item?.id) || selectAll}
                                        id={`checkbox-misc-${index}`}
                                      />
                                    </motion.div>
                                  )}
                                </Flex>
                              </Card>
                            </motion.div>
                          </Col>
                        ))}
                      </Row>
                    </motion.div>
                  ),
                },
              ]
            : []),
        ]}
      />
    </motion.div>
  );
};

export default CommionPermissonsTab;
